import React, { useState } from 'react';
import { Box, Button, Paper, Typography, CircularProgress } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { BRAND } from '../../config/brand';
import { formatAccessDateTime } from './formatAccessDateTime';

interface SalesAccessGuardProps {
  children: React.ReactNode;
}

/**
 * Hızlı satış, restoran ve paket ekranlarını sarar.
 * active/none: ek DOM yok (Fragment).
 * grace: sabit ince bant; satış ekranı kaymaz, çocuk unmount edilmez.
 * expired: tıklamayı engelleyen kaplama; sepet/yerel durum durur.
 * Durum yenilenince kaplama kendiliğinden kalkar.
 */
const SalesAccessGuard: React.FC<SalesAccessGuardProps> = ({ children }) => {
  const { accessState, graceUntil, refreshCompanyAccess } = useAuth();
  const { t, i18n } = useTranslation();
  const [checking, setChecking] = useState(false);

  const grace = accessState === 'grace';
  const expired = accessState === 'expired';

  const handleRecheck = async () => {
    setChecking(true);
    try {
      await refreshCompanyAccess();
    } finally {
      setChecking(false);
    }
  };

  const graceDate = formatAccessDateTime(graceUntil, i18n.language);

  return (
    <>
      {children}
      {grace && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1600,
            bgcolor: 'error.main',
            color: 'common.white',
            px: 2,
            py: 0.5,
            textAlign: 'center',
            fontSize: 13,
            fontWeight: 600,
            lineHeight: 1.35,
            pointerEvents: 'none',
          }}
        >
          {t('accessLock.salesGrace', { date: graceDate || '—' })}
        </Box>
      )}
      {expired && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 2000,
            bgcolor: 'rgba(0,0,0,0.72)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
          }}
        >
          <Paper sx={{ maxWidth: 440, width: '100%', p: 3, textAlign: 'center', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              {t('accessLock.salesExpiredTitle')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhoneIcon fontSize="small" color="primary" />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {BRAND.contact.customerService}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon fontSize="small" color="primary" />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {BRAND.contact.email}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              onClick={handleRecheck}
              disabled={checking}
              sx={{ textTransform: 'none' }}
            >
              {checking ? <CircularProgress size={18} color="inherit" /> : t('accessLock.recheck')}
            </Button>
          </Paper>
        </Box>
      )}
    </>
  );
};

export default SalesAccessGuard;
